import { NavLink, useNavigate, Outlet } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { AIPanel } from "./AIPanel";
import { CommandPalette, usePalette } from "./CommandPalette";
import { SquaresFour, Users, Kanban, Files, CheckSquare, ClockCounterClockwise, SignOut, MagnifyingGlass, Buildings, GearSix, Desktop, ChartBar, Newspaper, Robot } from "@phosphor-icons/react";

const nav = [
  { to: "/dashboard", label: "Dashboard", icon: SquaresFour, end: true },
  { to: "/workbench", label: "Workbench", icon: Desktop },
  { to: "/clients", label: "Clients", icon: Users },
  { to: "/pipeline", label: "Pipeline", icon: Kanban },
  { to: "/documents", label: "Documents", icon: Files },
  { to: "/insights", label: "Insights", icon: ChartBar },
  { to: "/drake/metrics", label: "Drake Metrics", icon: Robot },
  { to: "/content", label: "Content", icon: Newspaper },
  { to: "/approvals", label: "Approvals", icon: CheckSquare },
  { to: "/audit", label: "Audit Log", icon: ClockCounterClockwise },
  { to: "/settings", label: "Settings", icon: GearSix },
];

export function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { open, setOpen } = usePalette();

  return (
    <div className="flex min-h-screen bg-zinc-50">
      <CommandPalette open={open} setOpen={setOpen} />
      <aside className="w-60 shrink-0 border-r border-zinc-200 bg-white flex flex-col h-screen sticky top-0">
        <div className="p-4 border-b border-zinc-200 flex items-center gap-2">
          <img src="/artha-logo.png" alt="Artha" className="w-9 h-9 object-contain" />
          <div>
            <p className="font-heading font-bold text-sm leading-tight" style={{ color: "#0F2A5C" }}>Artha</p>
            <p className="text-[10px] text-zinc-500">Smarter access to your financial world</p>
          </div>
        </div>
        <button data-testid="open-command-palette-btn" onClick={() => setOpen(true)}
          className="mx-3 mt-3 flex items-center gap-2 rounded-md border border-zinc-200 bg-zinc-50 px-3 py-1.5 text-xs text-zinc-500 hover:bg-zinc-100 transition-colors duration-150">
          <MagnifyingGlass size={14} /> Search… <kbd className="ml-auto font-mono text-[10px] bg-white border border-zinc-200 rounded px-1">⌘K</kbd>
        </button>
        <nav className="flex-1 p-3 space-y-0.5">
          {nav.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.end} data-testid={`nav-${n.label.toLowerCase().replace(/ /g, "-")}`}
              className={({ isActive }) => `flex items-center gap-2.5 rounded-md px-3 py-2 text-sm transition-colors duration-150 ${isActive ? "bg-zinc-900 text-white" : "text-zinc-600 hover:bg-zinc-100"}`}>
              <n.icon size={16} /> {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-zinc-200">
          <div className="flex items-center gap-2 px-2 mb-2">
            <div className="w-7 h-7 rounded-full bg-zinc-200 flex items-center justify-center text-xs font-medium text-zinc-700">
              {user?.name?.[0]}
            </div>
            <div className="min-w-0">
              <p className="text-xs font-medium text-zinc-900 truncate">{user?.name}</p>
              <p className="text-[10px] text-zinc-500 uppercase">{user?.role}</p>
            </div>
          </div>
          <button data-testid="logout-btn" onClick={async () => { await logout(); navigate("/login"); }}
            className="flex w-full items-center gap-2 rounded-md px-3 py-1.5 text-xs text-zinc-500 hover:bg-zinc-100 transition-colors duration-150">
            <SignOut size={14} /> Sign out
          </button>
        </div>
      </aside>
      <main className="flex-1 min-w-0 overflow-x-hidden">
        <Outlet />
      </main>
      <AIPanel />
    </div>
  );
}
